/**
 * Trusted Devices section for the My Preferences page.
 *
 * Lists active devices the user has marked as trusted (30-day skip-OTP cookie).
 * Users can revoke individual devices or "sign out everywhere".
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Trash2, ShieldCheck, Loader2, Laptop } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../ui/button';
import { formatSessionDateTime } from '../../utils/formatSessionDateTime';

function relTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
    return `${Math.round(diff / 86400)} d ago`;
  } catch { return '—'; }
}

export default function TrustedDevicesSection() {
  const { get, del, post } = useApi();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/auth/devices');
      setDevices(res.devices || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load trusted devices');
    } finally { setLoading(false); }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    if (!confirm('Revoke this device? It will need OTP again next sign-in.')) return;
    setBusyId(id);
    try {
      await del(`/auth/devices/${id}`);
      toast.success('Device revoked');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    } finally { setBusyId(null); }
  };

  const revokeAll = async () => {
    if (!confirm('Sign out of every browser? You will need to re-enter OTP everywhere.')) return;
    setBusyId('all');
    try {
      const res = await post('/auth/devices/revoke-all', {});
      toast.success(`Revoked ${res.count || 0} device(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke-all failed');
    } finally { setBusyId(null); }
  };

  return (
    <section className="space-y-3" data-testid="trusted-devices-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />Trusted devices
          </h2>
          <p className="text-xs text-muted-foreground">
            Devices that can skip OTP for 30 days. Revoke any browser you don't recognize.
          </p>
        </div>
        {devices.length > 0 && (
          <Button
            variant="ghost" size="sm" className="text-red-600"
            onClick={revokeAll} disabled={busyId === 'all'}
            data-testid="revoke-all-devices-btn"
          >
            {busyId === 'all' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Sign out everywhere
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground italic">Loading…</p>
      ) : devices.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No trusted devices yet. Tick "Trust this device for 30 days" at sign-in to skip OTP next time.
        </p>
      ) : (
        <ul className="border rounded-lg divide-y">
          {devices.map(d => (
            <li
              key={d.device_id}
              className={
                'flex items-start gap-3 p-3 text-sm '
                + (d.is_current_device ? 'bg-emerald-500/5' : '')
              }
              data-testid={`device-row-${d.device_id}`}
            >
              <Laptop className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium truncate">{d.label || 'Unknown device'}</p>
                  {d.is_current_device && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold uppercase tracking-wider"
                      data-testid="current-device-badge"
                    >
                      <ShieldCheck className="w-3 h-3" />This device
                    </span>
                  )}
                </div>
                {/* Absolute timestamp — surfaces *why* the user just skipped
                    OTP. Relative time stays as a soft secondary on the same
                    line so habitual scanners still see "just now / 2 hr ago". */}
                <p className="text-[12px] text-foreground/80" data-testid="device-last-login">
                  Last login · {formatSessionDateTime(d.last_used_at) || '—'}
                  <span className="text-muted-foreground"> ({relTime(d.last_used_at)})</span>
                </p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {d.ip_address || 'unknown ip'} · expires {new Date(d.expires_at).toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="ghost" size="sm" className="text-red-600 shrink-0"
                onClick={() => revoke(d.device_id)} disabled={busyId === d.device_id}
                data-testid={`revoke-device-${d.device_id}`}
              >
                {busyId === d.device_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
